import { useState, useCallback, useMemo } from 'react';
import api from '../config/api';

export const useItinerary = (tripId, onItineraryChange, { usePartnerAuth = false } = {}) => {
  const authConfig = useMemo(
    () => (usePartnerAuth ? { usePartnerAuth: true } : {}),
    [usePartnerAuth]
  );
  const [itinerary, setItinerary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // On a group trip the organizer also sees travelers' own items, which live on their trips.
  const itemPath = useCallback((itemId) => {
    const ownerTripId = itinerary.find((item) => item.id === itemId)?.traveler_trip_id || tripId;
    return `/trips/${ownerTripId}/itinerary/${itemId}`;
  }, [itinerary, tripId]);

  const fetchItinerary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 FRONTEND - Fetching itinerary for trip:', tripId);
      const response = await api.get(`/trips/${tripId}/itinerary?t=${Date.now()}`, authConfig);
      
      console.log('🔍 FRONTEND - Raw API response:', response.data);
      console.log('🔍 FRONTEND - Response data length:', response.data?.length);
      
      // Debug: Check if cost_currency is in the response
      if (response.data && response.data.length > 0) {
        console.log('🔍 FRONTEND - First item keys:', Object.keys(response.data[0]));
        console.log('🔍 FRONTEND - First item cost_currency:', response.data[0].cost_currency);
      }
      
      // Debug transportation items specifically
      const transportItems = response.data?.filter(item => item.category === 'transport') || [];
      console.log('🔍 FRONTEND - Transportation items found:', transportItems.length);
      transportItems.forEach((item, index) => {
        console.log(`🔍 FRONTEND - Transport item ${index + 1}:`, {
          id: item.id,
          name: item.name,
          cost: item.cost,
          cost_currency: item.cost_currency,
          transportation_type: item.transportation_type,
          transportation_data: item.transportation_data
        });
      });
      
      setItinerary(response.data);
    } catch (err) {
      console.error('=== ERROR IN useItinerary.fetchItinerary ===');
      console.error('Error:', err);
      console.error('Response data:', err.response?.data);
      
      setError('Failed to fetch itinerary');
      console.error('Error fetching itinerary:', err);
    } finally {
      setLoading(false);
    }
  }, [tripId, authConfig]);

  const addItineraryItem = useCallback(async (itemData) => {
    try {
      setError(null);
      
      const response = await api.post(`/trips/${tripId}/itinerary`, itemData, authConfig);
      
      setItinerary(prev => {
        const newItinerary = [...prev, response.data].sort((a, b) => 
          new Date(a.start_datetime) - new Date(b.start_datetime)
        );
        return newItinerary;
      });
      
      // Notify parent component that itinerary has changed
      if (onItineraryChange) {
        onItineraryChange();
      }
      
      return response.data;
    } catch (err) {
      console.error('=== ERROR IN useItinerary.addItineraryItem ===');
      console.error('Error:', err);
      console.error('Response data:', err.response?.data);
      console.error('Status:', err.response?.status);
      
      setError(err.response?.data?.error || 'Failed to add itinerary item');
      throw err;
    }
  }, [tripId, onItineraryChange, authConfig]);

  const updateItineraryItem = useCallback(async (itemId, itemData) => {
    try {
      setError(null);
      console.log('=== UPDATING ITINERARY ITEM ===');
      console.log('Trip ID:', tripId);
      console.log('Item ID:', itemId);
      console.log('Item data being sent:', itemData);
      console.log('Cost field in data:', itemData.cost);
      
      const response = await api.put(itemPath(itemId), itemData, authConfig);
      console.log('Update API response:', response);
      console.log('Updated item data:', response.data);
      
      setItinerary(prev => {
        const newItinerary = prev.map(item => 
          item.id === itemId ? { ...item, ...response.data } : item
        ).sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
        return newItinerary;
      });
      
      // Notify parent component that itinerary has changed
      if (onItineraryChange) {
        onItineraryChange();
      }
      
      return response.data;
    } catch (err) {
      console.error('=== ERROR IN useItinerary.updateItineraryItem ===');
      console.error('Error:', err);
      console.error('Response data:', err.response?.data);
      console.error('Status:', err.response?.status);
      
      setError(err.response?.data?.error || 'Failed to update itinerary item');
      throw err;
    }
  }, [tripId, itemPath, onItineraryChange, authConfig]);

  const deleteItineraryItem = useCallback(async (itemId) => {
    try {
      setError(null);
      await api.delete(itemPath(itemId), authConfig);
      setItinerary(prev => prev.filter(item => item.id !== itemId));
      
      // Notify parent component that itinerary has changed
      if (onItineraryChange) {
        onItineraryChange();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete itinerary item');
      throw err;
    }
  }, [itemPath, onItineraryChange, authConfig]);

  const setItemVisibility = useCallback(async (itemId, clientVisible) => {
    const response = await api.patch(
      `${itemPath(itemId)}/visibility`,
      { client_visible: clientVisible },
      authConfig
    );
    setItinerary((prev) => prev.map((item) => (
      item.id === itemId ? { ...item, ...response.data } : item
    )));
    if (onItineraryChange) onItineraryChange();
    return response.data;
  }, [itemPath, onItineraryChange, authConfig]);

  const publishAllDrafts = useCallback(async () => {
    const response = await api.patch(`/trips/${tripId}/itinerary/publish-all`, {}, authConfig);
    setItinerary(response.data);
    if (onItineraryChange) onItineraryChange();
    return response.data;
  }, [tripId, onItineraryChange, authConfig]);

  const addNote = useCallback(async (itemId, note) => {
    try {
      setError(null);
      const response = await api.post(`${itemPath(itemId)}/notes`, { note }, authConfig);
      setItinerary(prev => prev.map(item => 
        item.id === itemId 
          ? { ...item, notes: [...(item.notes || []), response.data] }
          : item
      ));
      return response.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add note');
      throw err;
    }
  }, [itemPath, authConfig]);

  const deleteNote = useCallback(async (itemId, noteId) => {
    try {
      setError(null);
      await api.delete(`${itemPath(itemId)}/notes/${noteId}`, authConfig);
      setItinerary(prev => prev.map(item => 
        item.id === itemId 
          ? { ...item, notes: (item.notes || []).filter(note => note.id !== noteId) }
          : item
      ));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete note');
      throw err;
    }
  }, [itemPath, authConfig]);

  return {
    itinerary,
    loading,
    error,
    fetchItinerary,
    addItineraryItem,
    updateItineraryItem,
    deleteItineraryItem,
    setItemVisibility,
    publishAllDrafts,
    addNote,
    deleteNote,
  };
}; 